/**
 * CSRF protection for frontend API calls.
 *
 * Provides two mechanisms:
 * 1. `installCsrfFetchInterceptor()` — patches `window.fetch` to
 *    automatically add `X-Requested-With: XMLHttpRequest` to all
 *    same-origin `/api/` requests. Call once in `_app.tsx`.
 * 2. `apiFetch()` — explicit wrapper for use in contexts where
 *    the interceptor may not be active (e.g., tests).
 */

let interceptorInstalled = false

/**
 * Patches `window.fetch` to add the CSRF header to same-origin API requests.
 * Safe to call multiple times — only installs once.
 */
export function installCsrfFetchInterceptor(): void {
  if (typeof window === 'undefined' || interceptorInstalled) return
  interceptorInstalled = true

  const originalFetch = window.fetch.bind(window)

  window.fetch = function csrfFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.pathname
          : input.url

    // Only patch same-origin API requests
    if (url.startsWith('/api/')) {
      const headers = new Headers(init?.headers)
      if (!headers.has('X-Requested-With')) {
        headers.set('X-Requested-With', 'XMLHttpRequest')
      }
      return originalFetch(input, { ...init, headers })
    }

    return originalFetch(input, init)
  }
}

/**
 * Explicit fetch wrapper that includes the CSRF header.
 * Use this in tests or contexts where the global interceptor isn't active.
 */
export function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers)

  if (!headers.has('X-Requested-With')) {
    headers.set('X-Requested-With', 'XMLHttpRequest')
  }

  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  return fetch(input, { ...init, headers })
}
